import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useToastMessage } from "../../../components/ToastMessage/ToastMessage";
import { Toaster } from "@fluentui/react-components";
import { FormLoader } from "../../../components/Loader/FormLoader";
import { TableListLoader } from "../../../components/Loader/TableListLoader";
import { configFm } from "../../../types";
import { AppDispatch, RootState } from "../../../redux/store";

import FormAreaWrapper from "../../../components/Common/FormAreaWrapper";
import TableListWrapper from "../../../components/Common/TableListWrapper";

import {
  clearActiveItem,
  clearList,
  getVulnerabilityDetailById,
  getVulnerabilityListItems,
} from "../../../redux/Client/vulnerability";
import VulnerabilityFormWrapper from "../VulnerabilityFormWrapper";
import NoDataFound from "../../../components/NoDataFound/noDataFound";
import { useActiveRecordSync } from "../../../components/useActiveRecordSync";
const FORM_CONTEXT = "VULNERABILITY";

function VulnerabilityDetailPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { clientId, selectedItemId, action } = useParams<{
    clientId: string;
    selectedItemId?: string;
    action?: string;
  }>();
  const API_BASE_PATH = `/clients/${clientId}/vulnerability`;
  const { showToast, toasterId } = useToastMessage();
  const { activeItem, listItems, loading, activeItemloading } = useSelector(
    (state: RootState) => state.vulnerability
  );
  const formSettingsObj = useSelector((state: RootState) => state.formSettings);
  const [configForm, setConfigForm] = useState<configFm>();
  const [currentRecord, setCurrentRecord] = useState(1);
  useEffect(() => {
    if (clientId) {
      dispatch(getVulnerabilityListItems(clientId));
    }
  }, [dispatch, clientId]);
  useEffect(() => {
    if (listItems.length > 0 && selectedItemId) {
      dispatch(getVulnerabilityDetailById(selectedItemId));
    }
  }, [listItems, selectedItemId, dispatch]);

  // The effect above only refreshes `activeItem` when the URL carries an id
  // -- an id-less URL (action-only, e.g. the sidebar's bare vulnerability
  // link resolving to `.../vulnerability/view`) never runs it, so a
  // just-deleted record's `activeItem` was left stale even after `listItems`
  // itself had correctly refreshed. Re-check on every list change and
  // redirect to a real record's own id, same fix already shipped for
  // PartnerVulnerabilityDetailPage.tsx.
  useActiveRecordSync({
    listItems,
    idKey: "vulnerability_id",
    selectedId: selectedItemId,
    loading: loading || action === "add",
    onSelect: (item) =>
      navigate(`${API_BASE_PATH}/${item.vulnerability_id}/${action || "view"}`, {
        replace: true,
      }),
    onEmpty: () => dispatch(clearActiveItem()),
  });

  useEffect(() => {
    if (formSettingsObj.data?.formConfig) {
      const configObj = formSettingsObj.data.formConfig.find(
        (item: any) => item.context === FORM_CONTEXT
      );
      setConfigForm(configObj);
    }
  }, [formSettingsObj.data]);
  const loadData = async (
    dispatch: AppDispatch,
    id?: string
  ): Promise<void> => {
    if (clientId) {
      await dispatch(getVulnerabilityListItems(clientId));
      if (id) {
        await dispatch(getVulnerabilityDetailById(id));
      }
    }
  };

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      <div className="admin_container_flex">
        {(action === "add" || selectedItemId) && (
          <VulnerabilityFormWrapper
            activeCustomerId={clientId}
            mode={action}
            dataList={listItems}
            context={FORM_CONTEXT}
            notify={showToast}
            reloadData={loadData}
            section="vulnerability"
            apiPath={API_BASE_PATH}
            selectedRowItem={activeItem}
            activeRow={currentRecord}
            module="client"
          />
        )}

        {loading ? (
          <TableListLoader rows={8} columns={6} />
        ) : (
          listItems.length > 0 && (
            <TableListWrapper
              data={listItems}
              context={FORM_CONTEXT}
              selectedRowItem={activeItem}
              onRowAction={(action, row) =>
                navigate(
                  `${API_BASE_PATH}/${row?.vulnerability_id}/${
                    action || "view"
                  }`
                )
              }
              currentRecord={(index) => setCurrentRecord(index)}
            />
          )
        )}
      </div>
    </>
  );
}

export default VulnerabilityDetailPage;
